// #12/#19 — "Unfinished Add State signings" for the Applied GA admin dashboard.
// Add State requests that started DocuSign but never finished (the agent dropped
// off). Admins can see them and email a reminder. Mirrors UnsignedContractRequest.
import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import {
  Datagrid,
  DateField,
  FunctionField,
  List,
  SearchInput,
  Show,
  SimpleShowLayout,
  useNotify,
  useRecordContext,
  useRefresh,
} from "react-admin";
import { addStateDocusignService } from "../../../services/docusign.service";

const fullName = (record) =>
  [record?.payload?.firstName, record?.payload?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim() || "(no name)";

const statusLabels = {
  sent: "Sent (awaiting signature)",
  delivered: "Opened",
  completed: "Signed (not submitted)",
  declined: "Cancelled by agent",
  voided: "Voided",
};

const SendReminderButton = () => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [busy, setBusy] = useState(false);
  if (!record) return null;

  const onClick = () => {
    setBusy(true);
    addStateDocusignService
      .sendReminder(record.id)
      .then(() => {
        notify("Reminder sent", { type: "success" });
        refresh();
      })
      .catch(() => notify("Could not send reminder", { type: "warning" }))
      .finally(() => setBusy(false));
  };

  return (
    <Box sx={{ my: 2 }}>
      <Button variant="contained" disabled={busy} onClick={onClick}>
        {busy ? "Sending..." : "Send reminder"}
      </Button>
    </Box>
  );
};

// #84 - free-text search: partial match on the signer's name, email, and NPN.
const unsignedAddStateFilters = [
  <SearchInput source="q" placeholder="Name, email, or NPN" alwaysOn />,
];

export const UnsignedAddStateRequestList = (props) => (
  <List
    {...props}
    perPage={25}
    sort={{ field: "createdAt", order: "DESC" }}
    filters={unsignedAddStateFilters}
  >
    <Datagrid rowClick="show">
      <DateField source="createdAt" label="Started" locale="en-US" options="short" />
      <FunctionField label="Agent" render={fullName} />
      <FunctionField label="Email" render={(r) => r?.payload?.email || r?.email} />
      <FunctionField
        label="Status"
        render={(r) => statusLabels[r.envelopeStatus] || r.envelopeStatus}
      />
      <FunctionField label="Reminders" render={(r) => (r.reminders || []).length} />
    </Datagrid>
  </List>
);

export const UnsignedAddStateRequestShow = (props) => (
  <Show {...props}>
    <SimpleShowLayout>
      <FunctionField label="Agent" render={fullName} />
      <FunctionField label="Email" render={(r) => r?.payload?.email || r?.email} />
      <FunctionField
        label="States to add"
        render={(r) => (r?.payload?.states || []).join(", ")}
      />
      <FunctionField
        label="Status"
        render={(r) => statusLabels[r.envelopeStatus] || r.envelopeStatus}
      />
      <DateField source="createdAt" label="Started" showTime locale="en-US" />
      <FunctionField
        label="Reminders sent"
        render={(r) =>
          (r.reminders || [])
            .map((rem) => `${rem.to} (${new Date(rem.sentAt).toLocaleString()})`)
            .join("; ") || "None"
        }
      />
      <Typography variant="body2" sx={{ mt: 1 }} />
      <SendReminderButton />
    </SimpleShowLayout>
  </Show>
);
